import { inject as service } from '@ember/service';
import { bool, alias } from '@ember/object/computed';
import Controller from '@ember/controller';
import EmObject, { action } from '@ember/object';
import { Task, task } from 'ember-concurrency';
import FormValidations from 'cio/utils/form-validations';
import UserPasswordValidations from 'cio/utils/validations/user-password';
import AlertsService from 'cio/services/alerts';
import ApiService, { ApiError } from 'cio/services/api';
import RouterService from '@ember/routing/router-service';

export default class NewPasswordController extends Controller {
  @service alerts!: AlertsService;
  @service api!: ApiService;
  @service router!: RouterService;

  queryParams = ['resetPasswordToken'];

  _validations!: FormValidations;
  _formValues = EmObject.create({
    password: '',
    passwordConfirmation: '',
  });
  _passwordTouched = false;
  _passwordConfirmationTouched = false;

  constructor() {
    super(...arguments);

    this._validations = FormValidations.create({
      validations: UserPasswordValidations,
      context: this,
      modelKey: '_formValues',
    });
  }

  @bool('_validations.errors.password') _passwordHasError!: boolean;

  @alias('reset_password_token') resetPasswordToken!: string;

  _submitTask = task(async (evt: SubmitEvent) => {
    evt.preventDefault();

    try {
      await this._validations.validateModel();
    } catch (e) {
      // `validateModel` already found and set validation errors
      // return early to avoid executing real submit logic
      return;
    }

    try {
      await this.api.post('set_password_by_token', {
        pathScope: 'global',
        ignoreErrors: true,
        data: { token: this.resetPasswordToken, password: this._formValues.password },
      });
      this.alerts.success('resetPassword.newPasswordSuccess', 'Your password was changed');
      this.router.transitionTo('login');
    } catch (e) {
      let errors = e.errors || [];

      if (errors.some((e: ApiError) => e.detail === 'invalid token')) {
        this.alerts.error('resetPassword.newPasswordError', 'Reset password token is invalid.');
      } else if (errors.some((e: ApiError) => e.detail === 'expired token')) {
        this.alerts.error(
          'resetPassword.newPasswordError',
          'Reset password token has expired, please request a new one.',
        );
      } else {
        this.alerts.error('resetPassword.newPasswordError', 'Problem setting new user password. Try again later.');
      }
    }
  });

  setDefaultProperties() {
    this.setProperties({
      _passwordTouched: false,
      _passwordConfirmationTouched: false,
      _formValues: EmObject.create({
        password: '',
        passwordConfirmation: '',
      }),
    });
  }

  @action
  onPasswordBlur() {
    this.set('_passwordTouched', this._passwordTouched || Boolean(this._formValues.password));
  }

  @action
  onConfirmPasswordInput(passwordConfirmation: string) {
    this._formValues.set('passwordConfirmation', passwordConfirmation);
    this.set('_passwordConfirmationTouched', true);
    this._validations.validateAttribute('passwordConfirmation');
  }

  @action
  onPasswordChange(validationTask: Task<unknown, [string]>, password: string) {
    this._formValues.set('password', password);
    validationTask.perform(password);
    this._validations.validateAttribute('password');
  }
}
